/** Legacy vendor webhooks a subscription replaces (Ventrata `octo/webhooks`:
 *  booking_update for BOOKING_UPDATE, order_update for ORDER_UPDATE), shown in
 *  the subscription drawer.
 *
 *  The vendor has no "disabled" state: a webhook disabled from here was
 *  DELETED at the vendor and its full configuration is kept on the
 *  subscription, so "Switch to log only & enable webhooks" can re-create it.
 *  Only *managed* webhooks — the ones pointing at this tenant/account's legacy
 *  receive URL — are ever touched; others on a shared API key are read-only.
 *
 *  Presentational: the drawer owns the query because its mode buttons depend
 *  on the same data (see `legacyWebhookCounts`).
 */
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { ConsoleApiError } from '@/api/client';
import type {
  LegacyWebhook,
  LegacyWebhookResult,
  LegacyWebhooksState,
} from '@/api/notificationSubscriptions';
import { formatDate } from '@/utils/format';

const mono = { fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' } as const;

export interface LegacyWebhookCounts {
  /** Managed + active + restorable: what a disable would delete. */
  disableable: LegacyWebhook[];
  /** Managed + active but with masked headers: kept, never deleted. */
  unrestorable: LegacyWebhook[];
  /** Deleted from here, configuration kept: what an enable would re-create. */
  disabled: LegacyWebhook[];
}

export function legacyWebhookCounts(state: LegacyWebhooksState | undefined): LegacyWebhookCounts {
  const managed = (state?.webhooks ?? []).filter((w) => w.managed);
  return {
    disableable: managed.filter((w) => w.status === 'active' && w.restorable),
    unrestorable: managed.filter((w) => w.status === 'active' && !w.restorable),
    disabled: managed.filter((w) => w.status === 'disabled'),
  };
}

interface Props {
  vendor: string;
  state: LegacyWebhooksState | undefined;
  isFetching: boolean;
  error: unknown;
  onRefresh: () => void;
}

export function LegacyWebhooksSection({ vendor, state, isFetching, error, onRefresh }: Props) {
  const apiError = error instanceof ConsoleApiError ? error : null;
  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          Legacy webhooks{state?.event ? ` (${state.event})` : ''}
        </Typography>
        <Tooltip title={`Fetch the ${vendor} webhooks again`}>
          <span>
            <IconButton
              size="small"
              onClick={onRefresh}
              disabled={isFetching}
              aria-label="Refresh legacy webhooks"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
        {vendor} webhooks on this API key for the same event. {vendor} has no &quot;disabled&quot;
        state: disabling deletes them there and keeps their configuration here for the restore.
      </Typography>

      {isFetching && !state && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      )}
      {apiError && !state && (
        <Alert severity="error">
          Couldn’t load the legacy webhooks
          {apiError.status === 502 ? '' : ` (${apiError.status})`}: {apiError.message}
        </Alert>
      )}
      {state?.vendor_error && (
        <Alert severity="warning">
          Could not reach {vendor} — showing only the webhooks disabled from here. (
          {state.vendor_error})
        </Alert>
      )}
      {state &&
        (state.webhooks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {state.vendor_error
              ? 'No webhooks disabled from here.'
              : `No ${state.event ?? ''} webhooks on this API key.`}
          </Typography>
        ) : (
          <Table size="small" sx={{ '& td, & th': { px: 1 } }} aria-label="Legacy webhooks">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Webhook id</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.webhooks.map((w) => (
                <TableRow key={`${w.status}-${w.id}`} sx={w.managed ? undefined : { opacity: 0.6 }}>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <WebhookStatusChip webhook={w} />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Box sx={mono}>{w.url}</Box>
                    {!w.managed && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Not managed — points at another tenant or account; never touched.
                      </Typography>
                    )}
                    {w.managed && w.status === 'active' && !w.restorable && (
                      <Typography variant="caption" color="warning.main" display="block">
                        {vendor} masked its header values, so it could not be restored — it is never
                        deleted from here.
                      </Typography>
                    )}
                    {w.status === 'disabled' && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Disabled {formatDate(w.disabled_ts)}
                        {w.disabled_by ? ` by ${w.disabled_by.email}` : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ ...mono, verticalAlign: 'top' }}>{w.id}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ))}
    </Stack>
  );
}

function WebhookStatusChip({ webhook }: { webhook: LegacyWebhook }) {
  return webhook.status === 'active' ? (
    <Chip size="small" color="success" label="active" sx={{ height: 22, fontSize: 12 }} />
  ) : (
    <Chip size="small" color="warning" label="disabled" sx={{ height: 22, fontSize: 12 }} />
  );
}

/** Per-webhook failures of one disable / enable run (`ok` false). */
export function LegacyWebhookOutcome({
  result,
  message,
  severity,
  onClose,
}: {
  result: LegacyWebhookResult | null;
  message: string;
  severity: 'warning' | 'error';
  onClose: () => void;
}) {
  const problems = (result?.webhooks ?? []).filter((w) => w.result !== 'ok');
  return (
    <Alert severity={severity} onClose={onClose}>
      {message}
      {result?.error && <Box sx={{ mt: 0.5 }}>{result.error}</Box>}
      {problems.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {problems.map((w) => (
            <li key={w.id}>
              <Box component="span" sx={mono}>
                {w.url ?? w.id}
              </Box>{' '}
              — {w.result}
              {w.error ? `: ${w.error}` : ''}
            </li>
          ))}
        </Box>
      )}
    </Alert>
  );
}
